import type CleverTapType from "clevertap-web-sdk";

// CleverTap is currently OFF: the account ID is null, so init() never runs.
//
// It has been off for a while, but the SDK was still being pulled in with a
// top-level `require("clevertap-web-sdk")`, which webpack resolves statically.
// That put the entire 151 kB (gzipped) SDK into main.js on every page load,
// where it set window.clevertap, never initialised, and queued every
// event.push() into an object that never flushed to any account. 151 kB of a
// 732 kB bundle, doing nothing.
//
// The require is now a dynamic import() gated on the account ID, so webpack
// splits it into its own chunk that is only fetched when CleverTap is actually
// switched on. While it is off, the SDK is not downloaded at all.
//
// TO SWITCH CLEVERTAP BACK ON: restore the env read on the next line. The SDK
// then loads asynchronously and initialises exactly as before.
//
// One caveat if you do re-enable it: `export default clevertap` captures the
// value at module-evaluation time, so the default export stays null even after
// the async load resolves. That is fine for every current consumer, because
// they all read window.clevertap (see getCleverTap() in utils/analytics.ts,
// and the `typeof clevertap !== "undefined"` guards in
// NotificationPermissionPrompt and SubscriptionModal), and the loader below
// sets window.clevertap before it initialises. Do not add a new consumer that
// relies on the default export being non-null.
const CLEVERTAP_ACCOUNT_ID: string | null = null; // process.env.REACT_APP_CLEVERTAP_ACCOUNT_ID || null;
const rawRegion = process.env.REACT_APP_CLEVERTAP_REGION || "global";
const CLEVERTAP_REGION = rawRegion === "global" ? "eu1" : rawRegion;

export let isCleverTapInitialized = false;

let clevertap: typeof CleverTapType | null = null;

const isPrerendering =
  typeof navigator !== "undefined" && navigator.userAgent.includes("ReactSnap");

if (typeof window !== "undefined" && !isPrerendering && CLEVERTAP_ACCOUNT_ID) {
  import("clevertap-web-sdk")
    .then((mod) => {
      const ct = ((mod as any).default || mod) as typeof CleverTapType;
      clevertap = ct;
      (window as any).clevertap = ct;

      try {
        ct.spa = true;
        ct.init(CLEVERTAP_ACCOUNT_ID, CLEVERTAP_REGION);

        ct.privacy.push({ optOut: false });
        ct.privacy.push({ useIP: true });

        if (process.env.NODE_ENV === "development") {
          ct.setLogLevel(3);
        }

        if (typeof ct.event?.push === "function") {
          isCleverTapInitialized = true;
          console.log("[CleverTap] ✅ Initialized successfully");

          try {
            ct.event.push("CleverTap Initialized", {
              Environment: process.env.NODE_ENV || "unknown",
              source: "web_app",
              Timestamp: new Date().toISOString(),
            });
          } catch (testErr) {
            console.warn("[CleverTap] ⚠️ Test event failed:", testErr);
          }
        } else {
          console.error(
            "[CleverTap] ❌ Initialization failed: event.push not available",
          );
        }
      } catch (err) {
        console.error("[CleverTap] ❌ Initialization error:", err);
      }
    })
    .catch((err) => {
      // Never let a CleverTap load failure affect the app.
      console.error("[CleverTap] ❌ SDK failed to load:", err);
    });
}

export default clevertap;
